// A project's level (Brief 4 / P8) — the middle of the left→right descent, now
// a CHAT like every other level. Pick a project in the sidebar and you land in
// its room: the immovable goals anchored at the top (collapsed, so the
// conversation leads), the project chat as the body you talk in, and the
// sessions filed under it as a compact rail at the foot — the cards you drop
// into. Project id 0 is the "Unassigned" bucket: not a real project, so it has
// no room — just its list.

import { useParams } from "react-router-dom";
import { usePresenceContext } from "@waytide/ui";
import {
  UNASSIGNED_PROJECT_ID,
  sessionsInProject,
} from "@waytide/ui";
import { useProjectsContext } from "../projects/ProjectsContext";
import { projectName } from "../projects/projectModel";
import { ProjectGoals } from "../brain/ProjectGoals";
import { SessionCell } from "./SessionCell";
import { useProjectRoom } from "../projects/useProjectRoom";
import { useChannel } from "@waytide/ui";
import { Conversation } from "../Conversation";

export function ProjectSessions() {
  const { projectId } = useParams();
  const id = Number(projectId) || UNASSIGNED_PROJECT_ID;
  const { sessions, loaded } = usePresenceContext();
  const { projects } = useProjectsContext();
  const nowMs = Date.now();
  const mine = sessionsInProject(sessions, id);
  const name = projectName(projects, id);
  const isUnassigned = id === UNASSIGNED_PROJECT_ID;

  // Every level is a chat: a real project has its own room (a group thread). The
  // Unassigned bucket is a holding pen, not a room, so it gets no chat.
  const {
    threadId,
    loading: roomLoading,
    error: roomError,
  } = useProjectRoom(isUnassigned ? null : id);
  const { messages, send, pending, retry } = useChannel(threadId);

  const sessionsCount = !loaded
    ? "Reading sessions…"
    : mine.length === 0
      ? isUnassigned
        ? "No unassigned sessions"
        : "No sessions here yet"
      : `${mine.length} session${mine.length === 1 ? "" : "s"}`;

  const sessionsBody =
    mine.length === 0 ? (
      <p className="wt-dash-empty">
        {isUnassigned
          ? "Sessions with no project land here. Open one and assign it to a project."
          : "When a session is assigned to this project, it appears here."}
      </p>
    ) : (
      <div className="wt-session-grid" data-testid="session-grid">
        {mine.map((s) => (
          <SessionCell key={s.thread_id} session={s} nowMs={nowMs} />
        ))}
      </div>
    );

  // Unassigned: a plain list, full height — no goals, no room.
  if (isUnassigned) {
    return (
      <div className="wt-dash-pane wt-project">
        <header className="wt-dash-pane__head">
          <h1 className="wt-dash-pane__title">{name}</h1>
          <p className="wt-dash-pane__sub">Sessions with no project yet.</p>
        </header>
        <div className="wt-project__sessions wt-project__sessions--full">
          {sessionsBody}
        </div>
      </div>
    );
  }

  return (
    <div className="wt-dash-pane wt-project">
      <header className="wt-dash-pane__head">
        <h1 className="wt-dash-pane__title">{name}</h1>
        <p className="wt-dash-pane__sub">Project room — everyone here.</p>
      </header>

      {/* The immovable goals/spec — the alignment anchor. Collapsed by default
          here so the conversation leads; the header still offers "Set goals". */}
      <ProjectGoals projectId={id} defaultOpen={false} />

      {/* The project chat — talk through the goal here. */}
      {roomError ? (
        <p className="wt-dash-empty">Couldn't open this room: {roomError}</p>
      ) : roomLoading ? (
        <p className="wt-dash-empty">Opening {name}'s room…</p>
      ) : (
        <Conversation
          messages={messages}
          onSend={(b) => void send(b)}
          placeholder={`Message the ${name} room…`}
          emptyHint={`This is ${name}'s room — notes, decisions, and context for the goal. The sessions you assign show up below. (The agent and Operators reply inside a session, not here.)`}
          draftScope={threadId}
          pending={pending}
          onRetry={retry}
        />
      )}

      {/* The sessions filed under this project — the cards you drop into. A
          bounded foot rail under the conversation, so the chat keeps its room. */}
      <div className="wt-project__sessions">
        <div className="wt-project__sessions-head">{sessionsCount}</div>
        {sessionsBody}
      </div>
    </div>
  );
}
